/**
 * The display units chosen in Settings › Units, for leaf components: `useUnits()` instead of each one reading the
 * settings. Values stay metric everywhere else (DB, API, MCP); these only convert at display and input time.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useSettings } from '@/api/hooks';
import {
  fmtTemp,
  fmtTempRange,
  fmtWeight,
  fmtWeightIn,
  tempFromInput,
  tempRange,
  tempToDisplay,
  tempUnitLabel,
  weightFromInput,
  weightToDisplay,
  weightUnitLabel,
  type TemperatureUnit,
  type WeightUnit,
} from './format';

/** A one-tap bag size: what it stores and what its chip says. */
export interface BagSize {
  g: number;
  label: string;
}

export interface Units {
  temperature: TemperatureUnit;
  /** "°C" or "°F". */
  tempUnit: string;
  /** Stored °C -> "93 °C" / "199 °F" ('–' for none). */
  fmtTemp: (c: number | null | undefined) => string;
  /** Stored °C -> the number to show in the selected unit. */
  tempToDisplay: (c: number) => number;
  /** A number entered in the selected unit -> °C to store (see tempFromInput). */
  tempFromInput: (value: number, keepC?: number | null) => number;
  /** Valid range in the selected unit. */
  tempRange: { min: number; max: number };
  /** "0–100 °C" / "32–212 °F". */
  tempRangeText: string;

  /**
   * The unit for bag, stock and milk weights. Dose, yield, flow, live readouts and vessels are always grams (plain
   * fmtWeight), so only use these for bag sizes, remaining stock, the low-stock threshold and milk amounts.
   */
  weight: WeightUnit;
  /** "g" or "oz". */
  weightUnit: string;
  /** Stored grams -> "340 g" (`gDecimals` in grams, default 0) / "12.0 oz" ('–' for none). */
  fmtWeight: (g: number | null | undefined, gDecimals?: number) => string;
  /** Stored grams -> the number as text without the unit: "340" / "12.0" ('–' for none), for a Stat's value. */
  weightText: (g: number | null | undefined, gDecimals?: number) => string;
  /** Stored grams -> the number to show in the selected unit (grams unchanged, oz to 0.1). */
  weightToDisplay: (g: number) => number;
  /** A number entered in the selected unit -> grams to store (see weightFromInput). */
  weightFromInput: (value: number, keepG?: number | null) => number;
  /** The bag-size chips: 250 g / 500 g / 1 kg, or 12 oz / 1 lb / 2 lb. */
  bagSizes: readonly BagSize[];
}

const BAG_SIZES_G: readonly BagSize[] = [
  { g: 250, label: '250 g' },
  { g: 500, label: '500 g' },
  { g: 1000, label: '1 kg' },
];
const BAG_SIZES_OZ: readonly BagSize[] = [
  { g: weightFromInput(12, 'oz'), label: '12 oz' },
  { g: weightFromInput(16, 'oz'), label: '1 lb' },
  { g: weightFromInput(32, 'oz'), label: '2 lb' },
];

export function makeUnits(temperature: TemperatureUnit = 'c', weight: WeightUnit = 'g'): Units {
  return {
    temperature,
    tempUnit: tempUnitLabel(temperature),
    fmtTemp: (c) => fmtTemp(c, temperature),
    tempToDisplay: (c) => tempToDisplay(c, temperature),
    tempFromInput: (value, keepC) => tempFromInput(value, temperature, keepC),
    tempRange: tempRange(temperature),
    tempRangeText: fmtTempRange(temperature),

    weight,
    weightUnit: weightUnitLabel(weight),
    fmtWeight: (g, gDecimals = 0) => fmtWeightIn(g, weight, gDecimals),
    weightText: (g, gDecimals = 0) => (weight === 'oz' ? fmtWeight(g == null ? g : weightToDisplay(g, 'oz'), 1, '') : fmtWeight(g, gDecimals, '')),
    weightToDisplay: (g) => weightToDisplay(g, weight),
    weightFromInput: (value, keepG) => weightFromInput(value, weight, keepG),
    bagSizes: weight === 'oz' ? BAG_SIZES_OZ : BAG_SIZES_G,
  };
}

const METRIC = makeUnits();

// Metric until the settings arrive, and outside a provider (tests rendering a single component).
const UnitsContext = createContext<Units>(METRIC);

/** Fixed units, for tests, previews and the settings form (which shows its unsaved choice). */
export function UnitsProvider({ temperature = 'c', weight = 'g', children }: { temperature?: TemperatureUnit; weight?: WeightUnit; children: ReactNode }) {
  const units = useMemo(() => makeUnits(temperature, weight), [temperature, weight]);
  return <UnitsContext.Provider value={units}>{children}</UnitsContext.Provider>;
}

/** The units from the server-side settings (the app root renders this once). */
export function SettingsUnitsProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useSettings();
  return (
    <UnitsProvider temperature={settings?.temperatureUnit} weight={settings?.weightUnit}>
      {children}
    </UnitsProvider>
  );
}

export function useUnits(): Units {
  return useContext(UnitsContext);
}
